/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import { Formik, FormikProps, useField } from 'formik';
import moment from 'moment';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Card, Dropdown, DropdownItemProps } from 'semantic-ui-react';

import { AppStateContext } from 'api/AppStateContext';
import {
  TimeFrameEnumType,
  TimeframeOptionsFM,
  TimeframeSelectionResult,
} from 'models/TimeframeModels';
import { DefaultTimeframeStartDateType } from 'utils/constants';
import { formatDateISOUntilToday, reverseEngineerTimeframeOptionsFM } from 'utils/dateTimeHelpers';
import { isMobile, isNonMobile } from 'utils/responsive';
import { nameof, valueof } from 'utils/typeHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';
import { PanelScrimZIndex } from 'components/Panel';

import { CustomTimeframeControl } from './CustomTimeframeControl';
import { TimeframeItem } from './TimeframeItem';

const TfddWidth = '326px';

type TimeframeProps = {
  dateStr: string;
  onDateSelectionChanged: (dateSelection: TimeframeSelectionResult) => void;
  onCancel: () => void;
};

const timeframeModel = [
  {
    label: 'Last 30 days: ',
    value: valueof<TimeFrameEnumType>('30days'),
  },
  {
    label: 'Last 60 days: ',
    value: valueof<TimeFrameEnumType>('60days'),
  },
  {
    label: 'Last year: ',
    value: valueof<TimeFrameEnumType>('1year'),
  },
  {
    label: 'Custom timeframe: ',
    value: valueof<TimeFrameEnumType>('custom'),
  },
];

export const TimeframeDropdown: React.FC<TimeframeProps> = ({
  dateStr,
  onDateSelectionChanged,
  onCancel,
}) => {
  const timeframeOptions = useMemo<DropdownItemProps[]>(
    () =>
      timeframeModel &&
      timeframeModel.map(x => ({
        text: (
          <>
            <Typography inline variant="subtitle2" color="sea100">
              {x.label}
            </Typography>
            <Typography inline variant="subtitle2" color="stone90">
              {formatDateISOUntilToday(dateStr)}
            </Typography>
          </>
        ),
        value: x.value,
      })),
    [dateStr]
  );
  const { amendState } = useContext(AppStateContext);

  const [lastRememberedCustomDate, setlastRememberedCustomDate] = useState<Date>(null);
  const formInitialValues = useMemo(
    () => ({
      timeframeType: '60days',
      customDate: null,
      ...reverseEngineerTimeframeOptionsFM(dateStr),
    }),
    [dateStr]
  );

  useEffect(() => {
    setlastRememberedCustomDate(formInitialValues.customDate);
  }, [formInitialValues]);

  const submitPreset = (timeframeType: TimeFrameEnumType) => {
    // CODE: d2a88493: this does not handle custom dates, see onSubmit for custom dates
    const presetToSubmit =
      timeframeType === valueof<TimeFrameEnumType>('30days')
        ? 'last-30-days'
        : timeframeType === valueof<TimeFrameEnumType>('60days')
        ? 'last-60-days'
        : timeframeType === valueof<TimeFrameEnumType>('1year')
        ? 'last-year'
        : DefaultTimeframeStartDateType;
    onDateSelectionChanged &&
      onDateSelectionChanged({
        preset: presetToSubmit,
      });
    onCancel && onCancel();
    amendState({ isTimeframeDropdownExpanded: false });
  };

  const handleOnCancel = () => {
    amendState({
      isTimeframeDropdownExpanded: false,
      isCustomTimeframeDropdownExpanded: false,
    });
    onCancel && onCancel();
  };
  return (
    <Formik<TimeframeOptionsFM>
      enableReinitialize
      initialValues={formInitialValues}
      validate={values => {
        if (values.timeframeType === 'custom' && !values.customDate) {
          return { customDate: true };
        }
        return null;
      }}
      onSubmit={values => {
        // CODE: d2a88493: this function is only called from CustomTimeframeControl for custom dates. For other options see `submitPreset`
        const customDateToSubmit = moment(values.customDate);
        setlastRememberedCustomDate(customDateToSubmit.toDate());
        onDateSelectionChanged &&
          onDateSelectionChanged({
            isCustom: true,
            customDateStr: customDateToSubmit.format('YYYY-MM-DD'),
          });
        onCancel && onCancel();
        amendState({
          isTimeframeDropdownExpanded: false,
          isCustomTimeframeDropdownExpanded: false,
        });
      }}
    >
      {formikProps => (
        <TimeframeForm
          {...formikProps}
          lastRememberedCustomDate={lastRememberedCustomDate}
          timeframeOptions={timeframeOptions}
          handleOnCancel={handleOnCancel}
          submitPreset={submitPreset}
        />
      )}
    </Formik>
  );
};

const TimeframeForm: React.FC<
  FormikProps<TimeframeOptionsFM> & {
    timeframeOptions: DropdownItemProps[];
    handleOnCancel;
    submitPreset;
    lastRememberedCustomDate;
  }
> = ({
  values,
  isValid,
  submitForm,
  errors,
  setFieldValue,
  initialValues,
  resetForm,
  handleOnCancel,
  timeframeOptions,
  submitPreset,
  lastRememberedCustomDate,
}) => {
  const { appState, amendState } = useContext(AppStateContext);
  const { isTimeframeDropdownExpanded, isCustomTimeframeDropdownExpanded } = appState;
  const timeframeRootDropdownRef = React.useRef<any>();

  const isNonMobileDevice = isNonMobile(useBreakpointIndex());
  const isMobileDevice = isMobile(useBreakpointIndex());
  const [, , helpers] = useField(nameof<TimeframeOptionsFM>('customDate'));
  const { setError } = helpers;

  const activeOption =
    timeframeOptions && timeframeOptions.find(x => x.value === values.timeframeType);
  const activeOptionLastSelected =
    timeframeOptions && timeframeOptions.find(x => x.value === initialValues.timeframeType);

  useEffect(() => {
    if (!isCustomTimeframeDropdownExpanded) {
      resetForm();
    }
  }, [isCustomTimeframeDropdownExpanded]);

  return (
    <form autoComplete="off" sx={{ width: '100%' }} ref={timeframeRootDropdownRef}>
      <Dropdown
        sx={{
          '&.ui.dropdown': {
            position: 'relative',
            height: '100%',
            zIndex: PanelScrimZIndex + 1,
            width: [undefined, TfddWidth],
            textAlign: 'center',
          },
        }}
        fluid={isMobileDevice}
        open={isTimeframeDropdownExpanded}
        onBlur={e => {
          if (isNonMobileDevice) {
            const blurCauseTarget = (e as any).relatedTarget;
            if (!blurCauseTarget || !timeframeRootDropdownRef.current.contains(blurCauseTarget))
              handleOnCancel();
          }
        }}
        icon={null}
        trigger={
          <div
            onClick={() => {
              amendState({
                isTimeframeDropdownExpanded: !isTimeframeDropdownExpanded,
                isCustomTimeframeDropdownExpanded: false,
              });
            }}
            sx={{ padding: '8px 16px' }}
          >
            <Typography color="stone90" variant="body2" inline sx={{ mr: '6px' }}>
              {activeOptionLastSelected && activeOptionLastSelected.text}
            </Typography>
            <BdIcon
              name={
                isTimeframeDropdownExpanded || isCustomTimeframeDropdownExpanded
                  ? 'icon-chevron-up'
                  : 'icon-chevron-down'
              }
              color="stone90"
              bold
              nomargin
              sx={{
                '&.icon.bd-icon': {
                  verticalAlign: 'text-bottom',
                },
              }}
            />
          </div>
        }
      >
        <Dropdown.Menu
          className="timeframe__dropdown"
          sx={{
            ...(isNonMobileDevice && {
              '.ui.menu .ui.dropdown &.menu': {
                boxShadow: '0px 4px 15px rgba(60, 63, 77, 0.15)',
                border: 'none',
                width: [undefined, TfddWidth],
                marginTop: '3px',
                borderRadius: 0,
              },
            }),
          }}
        >
          <Dropdown.Header className="timeframe__dropdown">
            <Typography inline variant="caption" color="stone50">
              Select a timeframe
            </Typography>
          </Dropdown.Header>
          <Dropdown.Item onClick={() => submitPreset('30days')}>
            <TimeframeItem
              value={activeOption.value}
              timeframeType="30days"
              label="Last 30 days: "
            />
          </Dropdown.Item>
          <Dropdown.Item onClick={() => submitPreset('60days')}>
            <TimeframeItem
              value={activeOption.value}
              timeframeType="60days"
              label="Last 60 days: "
            />
          </Dropdown.Item>
          <Dropdown.Item onClick={() => submitPreset('1year')}>
            <TimeframeItem value={activeOption.value} timeframeType="1year" label="Last year: " />
          </Dropdown.Item>
          <Dropdown.Item
            onClick={e => {
              if (isCustomTimeframeDropdownExpanded) {
                setFieldValue(
                  nameof<TimeframeOptionsFM>('timeframeType'),
                  initialValues.timeframeType
                );
                amendState({ isCustomTimeframeDropdownExpanded: false });
              } else {
                setFieldValue(
                  nameof<TimeframeOptionsFM>('timeframeType'),
                  valueof<TimeFrameEnumType>('custom')
                );
                if (lastRememberedCustomDate)
                  setFieldValue(nameof<TimeframeOptionsFM>('customDate'), lastRememberedCustomDate);
                amendState({ isCustomTimeframeDropdownExpanded: true });
              }
              e.stopPropagation();
            }}
          >
            <FlexGroup
              gutter="0"
              suffix={
                <BdIcon
                  name="icon-chevron-right"
                  color="stone70"
                  bold
                  nomargin
                  sx={{
                    '&.icon.bd-icon': {
                      verticalAlign: 'bottom',
                    },
                  }}
                />
              }
            >
              <TimeframeItem
                value={activeOption.value}
                timeframeType="custom"
                customDate={values.customDate}
                label="Custom timeframe"
              />
            </FlexGroup>
          </Dropdown.Item>
          {isCustomTimeframeDropdownExpanded && (
            <div
              sx={{
                position: 'absolute',
                ...(isNonMobileDevice && {
                  top: '0',
                  left: TfddWidth,
                  width: TfddWidth,
                  boxShadow: '0 4px 15px rgba(60,63,77,.15)',
                }),
                ...(isMobileDevice && {
                  top: '-1px',
                  zIndex: PanelScrimZIndex + 20,
                  width: '100%',
                }),
              }}
            >
              <Card
                className="nomargin"
                fluid
                sx={{
                  border: 'none',
                }}
              >
                {isMobileDevice && (
                  <Card.Content textAlign="center">
                    <div sx={{ position: 'relative' }}>
                      <Typography inline variant="h6" color="stone90">
                        Custom Timeframe
                      </Typography>
                      <BdIcon
                        bold
                        nomargin
                        sx={{ position: 'absolute', left: 0, top: '3px' }}
                        name="icon-chevron-left"
                        color="sea100"
                        onClick={() => {
                          setFieldValue(
                            nameof<TimeframeOptionsFM>('timeframeType'),
                            initialValues.timeframeType
                          );
                          amendState({ isTimeframeDropdownExpanded: true });
                          amendState({ isCustomTimeframeDropdownExpanded: false });
                        }}
                      />
                    </div>
                  </Card.Content>
                )}
                <Card.Content>
                  <CustomTimeframeControl
                    fieldName={nameof<TimeframeOptionsFM>('customDate')}
                    label="Show cases and deaths starting from:"
                    dateErrors={errors.customDate}
                    autoFocusMonth
                    onCancel={() => {
                      setFieldValue(
                        nameof<TimeframeOptionsFM>('timeframeType'),
                        initialValues.timeframeType
                      );
                      handleOnCancel();
                    }}
                    onSubmit={() =>
                      isValid
                        ? submitForm()
                        : errors.customDate === true
                        ? setError('Please specify a valid date')
                        : undefined
                    }
                  />
                </Card.Content>
              </Card>
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </form>
  );
};
